import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calculator, Paperclip, Download } from 'lucide-react';
import { ExportPanel } from '../../components/accounting/ExportPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `${Number(n || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('client_portal_token')}` });
const firstOfMonth = () => new Date().toLocaleDateString('en-CA').slice(0, 8) + '01';
const today = () => new Date().toLocaleDateString('en-CA');

const openFile = async (url, name) => {
  try {
    const r = await axios.get(`${API_URL}${url}`, { headers: auth(), responseType: 'blob' });
    const href = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = href; a.target = '_blank'; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(href), 10000);
  } catch { toast.error('Fichier indisponible'); }
};

const Stat = ({ label, value, testId }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
    <p className="text-xs text-slate-400">{label}</p>
    <p className="text-xl font-bold text-white" data-testid={testId}>{value}</p>
  </div>
);

export const ClientAccountingTab = () => {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/saas/portal/client/accounting`, { headers: auth(), params: { date_from: from, date_to: to } })
      .then(r => setData(r.data)).catch(() => toast.error('Erreur de chargement'));
  }, [from, to]);

  const invoicesTotal = (data?.invoices || []).reduce((s, i) => s + (i.total || 0), 0);
  const expensesTotal = (data?.expenses || []).reduce((s, e) => s + (e.total_amount || 0), 0);
  const inputCls = 'bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white';

  return (
    <div className="space-y-6" data-testid="client-accounting-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Calculator size={20} /> Comptabilité</h2>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          Du <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputCls} data-testid="accounting-from" />
          au <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputCls} data-testid="accounting-to" />
        </div>
      </div>
      {!data ? <p className="text-slate-500 text-sm">Chargement…</p> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat label="Factures émises" value={money(invoicesTotal)} testId="accounting-invoices-total" />
            <Stat label="Dépenses" value={money(expensesTotal)} testId="accounting-expenses-total" />
            <Stat label="Écart" value={money(invoicesTotal - expensesTotal)} testId="accounting-balance" />
          </div>

          <ExportPanel baseUrl={`${API_URL}/api/saas/portal/client/accounting`} headers={auth()} fixedRange={[from, to]} />

          <section>
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Factures émises ({data.invoices.length})</h3>
            <div className="bg-slate-900 border border-slate-800 rounded-xl divide-y divide-slate-800">
              {data.invoices.map(i => (
                <div key={i.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 text-sm" data-testid={`accounting-invoice-${i.id}`}>
                  <span className="text-white font-medium w-36">{i.custom_invoice_number || i.invoice_number}</span>
                  <span className="text-slate-400 w-24">{(i.date || '').slice(0, 10)}</span>
                  <span className="text-slate-300 flex-1 min-w-[8rem]">{i.client_name}</span>
                  <span className="text-xs text-slate-500">{{ paid: 'Payée', overdue: 'En retard' }[i.status] || 'Envoyée'}</span>
                  <span className="text-white w-28 text-right">{money(i.total)}</span>
                  <button onClick={() => openFile(`/api/saas/portal/client/accounting/invoices/${i.id}/pdf`, `${i.invoice_number}.pdf`)}
                    className="text-amber-400 hover:text-amber-300" title="Télécharger le PDF" data-testid={`accounting-invoice-pdf-${i.id}`}><Download size={16} /></button>
                </div>
              ))}
              {!data.invoices.length && <p className="px-4 py-6 text-slate-500 text-sm text-center">Aucune facture sur cette période</p>}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Dépenses ({data.expenses.length})</h3>
            <div className="bg-slate-900 border border-slate-800 rounded-xl divide-y divide-slate-800">
              {data.expenses.map(e => (
                <div key={e.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 text-sm" data-testid={`accounting-expense-${e.id}`}>
                  <span className="text-slate-400 w-24">{e.date}</span>
                  <span className="text-white flex-1 min-w-[8rem]">{e.vendor || e.description || '—'}</span>
                  <span className="text-xs text-slate-500">{e.category}</span>
                  <span className="text-xs text-slate-500">Taxes {money(e.tax_amount)}</span>
                  <span className="text-white w-28 text-right">{money(e.total_amount)}</span>
                  {e.has_receipt ? (
                    <button onClick={() => openFile(`/api/saas/portal/client/accounting/receipts/${e.id}`, `recu_${e.id}`)}
                      className="text-amber-400 hover:text-amber-300" title="Voir le reçu" data-testid={`accounting-receipt-${e.id}`}><Paperclip size={16} /></button>
                  ) : <span className="w-4" />}
                </div>
              ))}
              {!data.expenses.length && <p className="px-4 py-6 text-slate-500 text-sm text-center">Aucune dépense sur cette période</p>}
            </div>
          </section>
        </>
      )}
    </div>
  );
};
